import { z } from "zod";
import type { PasswordPolicy } from "@yazamco/shared-types";

/**
 * Authentication validation (Phase 5.8).
 *
 * Boundary validation for the production local-auth endpoints. The password
 * policy is shared so the client can mirror the server's rules inline.
 */

/** Longest password accepted, so a huge input cannot become an Argon2 DoS. */
export const PASSWORD_MAX_LENGTH = 200;

/**
 * Platform password policy — LENGTH-FIRST.
 *
 * 15 characters, no character-class rules. Composition requirements (an
 * uppercase, a digit, a symbol) measurably push users towards predictable
 * mutations of a short word, which is exactly what a cracking wordlist expands
 * first; length is what actually costs an attacker work. Strength beyond length
 * is enforced by the breached-password check on the server
 * (`PASSWORD_BREACH_CHECK_ENABLED`), which rejects known-compromised
 * passphrases however well they satisfy a composition rule.
 *
 * `breachCheckEnabled` is filled in by the API from real configuration
 * (`buildPasswordPolicy`); the default here states the intended posture.
 */
export const PASSWORD_MIN_LENGTH = 15;

export const PASSWORD_POLICY: PasswordPolicy = {
  minLength: PASSWORD_MIN_LENGTH,
  maxLength: PASSWORD_MAX_LENGTH,
  breachCheckEnabled: true,
};

/** The policy as actually configured on this server. */
export function buildPasswordPolicy(breachCheckEnabled: boolean): PasswordPolicy {
  return { ...PASSWORD_POLICY, breachCheckEnabled };
}

/**
 * Validate a password against the LOCAL (offline) part of the policy. Returns an
 * error list (empty = ok). The breach check is a separate, asynchronous control —
 * it needs the network — so it is not folded in here; callers that set a password
 * must run both (see `assertPasswordAcceptable` in the API).
 */
export function passwordPolicyErrors(password: string): string[] {
  const errors: string[] = [];
  if (password.length < PASSWORD_POLICY.minLength) {
    errors.push(`at least ${PASSWORD_POLICY.minLength} characters`);
  }
  if (password.length > PASSWORD_POLICY.maxLength) {
    errors.push(`at most ${PASSWORD_POLICY.maxLength} characters`);
  }
  // Reject whitespace-only and empty-after-trim values: length alone would
  // otherwise accept 15 spaces.
  if (password.trim().length < PASSWORD_POLICY.minLength) {
    errors.push("at least 15 non-whitespace characters");
  }
  return errors;
}

/** A password that satisfies the policy (used when setting/creating). */
export const strongPasswordSchema = z
  .string()
  .max(PASSWORD_MAX_LENGTH)
  .refine((p) => passwordPolicyErrors(p).length === 0, {
    message: `password does not meet the policy (at least ${PASSWORD_MIN_LENGTH} characters)`,
  });

/** POST /api/v1/auth/login body. Login does NOT enforce the policy on input
 * (an old password may predate a policy change) — it only checks credentials. */
export const loginSchema = z.object({
  email: z.string().trim().email().max(320),
  password: z.string().min(1, "password is required").max(PASSWORD_MAX_LENGTH),
  rememberMe: z.boolean().optional(),
});

export type LoginInput = z.infer<typeof loginSchema>;

/** Input for creating/updating a local user (CLI / admin). */
export const createLocalUserSchema = z.object({
  email: z.string().trim().email().max(320),
  displayName: z.string().trim().min(1).max(200),
  role: z.enum(["Viewer", "Operator", "Administrator"]),
  password: strongPasswordSchema,
});

export type CreateLocalUserInput = z.infer<typeof createLocalUserSchema>;
